/* One intake owner for protected lead forms. Only a matching Worker acceptance
 * can produce success or measurement. No customer data enters analytics/storage. */
(() => {
  'use strict';
  if (window.HOKInquiry) return;
  const configNode = document.getElementById('hok-inquiry-config');
  const config = configNode?.dataset || {};
  let messages = {};
  try { messages = JSON.parse(configNode?.textContent || '{}'); } catch { /* Fallback copy remains usable. */ }
  const text = (key, fallback) => typeof messages?.[key] === 'string' ? messages[key] : fallback;
  const states = new WeakMap();
  const accepted = new Map();
  const memoryMeasured = new Set();
  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const TYPES = new Set(['contact', 'sculpture', 'solar', 'laser', 'industrial', 'koval_rendering', 'koval_price']);
  const MAX_PHOTO = 50 * 1024 * 1024;
  const machineFields = new Set(['contact[Inquiry reference]', 'contact[request_id]', 'contact[GCLID]', 'contact[Landing page]', 'contact[Marketing attribution]', 'contact[Source / campaign]']);
  const permission = method => { try { return window.Shopify?.customerPrivacy?.[method]?.() === true; } catch { return false; } };
  const preview = () => {
    const params = new URLSearchParams(location.search);
    return window.Shopify?.theme?.role !== 'main' || !!window.Shopify?.designMode || params.has('preview_theme_id') || params.get('hok_inquiry_test') === '1';
  };
  const field = (form, name) => form.elements.namedItem(name)?.value || '';
  const formType = form => form.dataset.hokInquiry === 'koval' ? (form.querySelector('[data-koval-purpose]')?.value === 'price' ? 'koval_price' : 'koval_rendering') : form.dataset.hokInquiry;
  let loader;
  function loadTurnstile() {
    if (window.turnstile?.render) return Promise.resolve(window.turnstile);
    if (loader) return loader;
    loader = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      const timer = setTimeout(() => finish(false), 20000);
      const finish = ok => {
        clearTimeout(timer);
        if (ok && window.turnstile?.render) resolve(window.turnstile);
        else { script.remove(); loader = null; reject(new Error('verification-unavailable')); }
      };
      script.onload = () => finish(true);
      script.onerror = () => finish(false);
      document.head.appendChild(script);
    });
    return loader;
  }
  async function token(form) {
    const turnstile = await loadTurnstile();
    return new Promise((resolve, reject) => {
      const container = document.createElement('div');
      container.setAttribute('aria-hidden', 'true');
      form.appendChild(container);
      let widget, settled = false;
      const timer = setTimeout(() => finish(null), 30000);
      const finish = value => {
        if (settled) return;
        settled = true; clearTimeout(timer);
        // Every attempt gets a new one-use token, including idempotent retries.
        queueMicrotask(() => { try { if (widget !== undefined) turnstile.remove(widget); } catch { /* Already removed. */ } container.remove(); });
        if (typeof value === 'string' && value.length) resolve(value); else reject(new Error('verification-unavailable'));
      };
      try {
        widget = turnstile.render(container, {
          sitekey: config.sitekey, action: 'hok_inquiry', execution: 'execute',
          'response-field': false, callback: finish,
          'error-callback': () => finish(null), 'expired-callback': () => finish(null),
          'timeout-callback': () => finish(null), 'unsupported-callback': () => finish(null)
        });
        turnstile.execute(widget);
      } catch { finish(null); }
    });
  }
  function status(form, state, message) {
    let node = form.querySelector('[data-hok-inquiry-status]');
    if (!node) {
      node = document.createElement('div'); node.className = 'hok-inquiry-status';
      node.dataset.hokInquiryStatus = ''; node.tabIndex = -1; form.appendChild(node);
    }
    node.hidden = false; node.dataset.state = state;
    node.setAttribute('role', state === 'error' ? 'alert' : 'status');
    node.textContent = message; form.dataset.hokInquiryState = state;
    if (state !== 'working') node.focus({ preventScroll: true });
  }
  function controls(form, busy) {
    const state = states.get(form);
    if (busy) {
      state.disabled = [...form.elements].map(node => [node, node.disabled]);
      state.disabled.forEach(([node]) => { node.disabled = true; });
      form.setAttribute('aria-busy', 'true');
    } else {
      state.disabled?.forEach(([node, disabled]) => { node.disabled = disabled; });
      delete state.disabled; form.removeAttribute('aria-busy');
      delete form.dataset.hqcSubmitting;
    }
  }
  function photoEntries(form) {
    return [...form.querySelectorAll('input[type="file"]')].filter(input => !input.matches(':disabled')).flatMap(input => [...(input.files || [])].map(file => [input.name || 'site_photos', file]));
  }
  function validate(form, type) {
    const photos = photoEntries(form);
    const fileInputs = [...form.querySelectorAll('input[type="file"]')];
    fileInputs.forEach(input => input.setCustomValidity(''));
    let error = '';
    if (photos.length > 3) error = text('photo_count', 'Please choose up to 3 site photos.');
    else if (photos.some(([, file]) => file.size > MAX_PHOTO)) error = text('photo_size', 'Each photo must be 50 MB or smaller.');
    else if (photos.some(([, file]) => !/\.(jpe?g|png|webp|heic|heif|gif|avif|tiff?|bmp)$/i.test(file.name) || file.size === 0)) error = text('photo_type', 'Please choose JPEG, PNG, WebP, HEIC, GIF, AVIF, TIFF or BMP images.');
    else if (type === 'koval_rendering' && photos.length === 0) error = text('photo_required', 'Please add at least one site photo for your rendering.');
    if (error) { fileInputs[0]?.setCustomValidity(error); status(form, 'error', error); return false; }
    if (!form.reportValidity()) return false;
    if (form.id === 'HoKSharedQuoteForm' && !field(form, 'contact[Equipment summary]').trim()) {
      status(form, 'error', text('equipment_required', 'Please select the equipment for your quote.')); return false;
    }
    return true;
  }
  function snapshot(form, type) {
    const pairs = [...new FormData(form)].filter(([key, value]) => key.startsWith('contact[') && typeof value === 'string' && !['contact[Inquiry reference]', 'contact[request_id]'].includes(key));
    const files = photoEntries(form);
    const fingerprint = JSON.stringify([type, pairs.filter(([key]) => !machineFields.has(key)), files.map(([key, file]) => [key, file.name, file.size, file.type, file.lastModified])]);
    return { pairs, files, fingerprint, website: field(form, 'website') };
  }
  function measurement(form, type, id) {
    const common = { page_location: location.origin + location.pathname, page_referrer: '', page_title: 'House of Koval inquiry' };
    const record = { id, type, preview: preview() || form.dataset.reviewOnly === 'true', created: Date.now(), ga: '', ads: '', name: '', detail: common };
    const marker = form.querySelector('output');
    const data = marker?.dataset || {};
    if (type === 'sculpture') {
      const pieces = { 'Seated Figure': 'seated-figure', 'Circular Fountain': 'circular-fountain', 'Faceted Fountain': 'faceted-fountain', 'Brushed Open Form': 'brushed-open-form', 'Mirror Stone Pair': 'mirror-stone-pair', 'Custom or original commission': 'commission', 'Help choosing': 'undecided' };
      const projects = { 'Home / garden': 'residential', 'Hospitality / commercial': 'commercial', 'Architect / designer / project team': 'design-trade', 'Other project': 'other' };
      Object.assign(record, { ga: data.stainlessGaDestination, ads: data.stainlessAdsDestination, name: 'sculpture_project_inquiry' });
      Object.assign(record.detail, { inquiry_id: id, product_family: 'stainless_steel_sculpture', piece: pieces[form.querySelector('[data-stainless-interest-field]')?.value] || 'undecided', project_type: projects[form.querySelector('[data-stainless-project-field]')?.value] || 'undecided' });
    } else if (type === 'solar') {
      Object.assign(record, { ga: data.solarGaDestination, ads: data.solarAdsDestination, name: 'solar_pricing_request' });
      Object.assign(record.detail, { inquiry_id: id, product_family: 'containerized_solar', package: ({ 'Solar + Storage': 'hybrid', 'Solar Array': 'solar-only', 'Different size / project': 'custom', 'Help me choose': 'undecided' })[form.querySelector('[data-solar-package-field]')?.value] || 'undecided' });
    } else if (type === 'laser') {
      Object.assign(record, { ga: data.laserGaDestination, ads: data.laserAdsDestination, name: data.laserGaEvent === 'laser_bird_site_inquiry' ? data.laserGaEvent : '' });
      Object.assign(record.detail, { inquiry_id: id, product_family: 'laser_bird_deterrent', planning_budget: ({ 'Around $5,495': 'mid', 'Around $12,495': 'premium', 'Compare both': 'compare', 'Not sure yet': 'undecided' })[form.querySelector('[data-laser-budget-field]')?.value] || 'undecided', site_type: ({ 'Commercial property': 'facilities', 'Vineyard or crop operation': 'vineyard', 'Pond, hatchery or animal operation': 'pond-animal', 'Other commercial site': 'other' })[form.querySelector('[data-laser-context-field]')?.value] || 'undecided' });
    } else if (type === 'industrial') {
      const skus = new Set(['SY-169336-L', 'SY-209336-L', 'SY-259336-L', 'SY-259330', 'EPT-2748-45-RP-AGM', 'S-62-AA', 'WL-100-12-812', 'HOK-HRAIL-KIT-1', 'HOK-HRAIL-KIT-10', 'HOK-HRAIL-KIT-100']);
      const models = [...new Set(field(form, 'contact[Model]').split(',').map(sku => sku.trim()))].filter(sku => skus.has(sku) && window.HOKCartConfig?.models?.[sku]?.vendor?.toLowerCase() === 'vestil');
      if (models.length) {
        Object.assign(record, { ga: data.hqcGaDestination, ads: data.hqcAdsDestination, name: 'industrial_delivered_quote_request' });
        Object.assign(record.detail, { inquiry_id: id, product_family: 'vestil_industrial', models: models.join(',') });
      }
    } else if (type.startsWith('koval_')) {
      const tracking = document.getElementById('koval-tracking-config')?.dataset || {};
      const rendering = type === 'koval_rendering';
      Object.assign(record, { ga: tracking.ga4Id, ads: tracking.adsId && `${tracking.adsId}/${rendering ? tracking.renderingLabel : tracking.priceLabel}`, name: 'generate_lead' });
      Object.assign(record.detail, { lead_type: rendering ? 'rendering' : 'price', currency: 'USD', value: rendering ? 300 : 150, transaction_id: id });
      const line = field(form, 'contact[Line]');
      if (/memorial|church/i.test(line)) record.detail.koval_sensitive_context = 'yes';
      else if (/^(estate|ranch|architectural|portrait|modern)$/.test(line)) record.eligible = true;
    }
    return record;
  }
  function measured(id, channel) {
    const key = `hok.inquiry.measured.${channel}.${id}`;
    if (memoryMeasured.has(key)) return true;
    try { return sessionStorage.getItem(key) === '1'; } catch { return false; }
  }
  function consume(id, channel) {
    const key = `hok.inquiry.measured.${channel}.${id}`;
    memoryMeasured.add(key);
    try { sessionStorage.setItem(key, '1'); } catch { /* In-page at-most-once still applies. */ }
  }
  function measure(record) {
    if (record.preview || preview() || Date.now() - record.created > 3600000 || typeof window.gtag !== 'function') return;
    const detail = { ...record.detail };
    if (record.eligible && permission('marketingAllowed')) detail.koval_remarketing_eligible = 'yes';
    if (/^G-[A-Z0-9]+$/.test(record.ga || '') && record.name && permission('analyticsProcessingAllowed') && !measured(record.id, 'ga')) {
      consume(record.id, 'ga');
      try { window.gtag('event', record.name, { ...detail, send_to: record.ga }); } catch { /* Never retry a possibly queued event. */ }
    }
    if (/^AW-\d+\/[A-Za-z0-9_-]+$/.test(record.ads || '') && permission('marketingAllowed') && permission('saleOfDataAllowed') && !measured(record.id, 'ads')) {
      consume(record.id, 'ads');
      try {
        window.gtag('config', record.ads.split('/')[0], { send_page_view: false });
        window.gtag('event', 'conversion', { ...detail, send_to: record.ads, transaction_id: record.id });
      } catch { /* Never retry a possibly queued event. */ }
    }
  }
  async function requestJSON(url, init = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await fetch(url, { credentials: 'omit', ...init, signal: controller.signal });
      return { response, receipt: await response.json() };
    } finally { clearTimeout(timer); }
  }
  function intakeURL(value, endpoint) {
    const url = new URL(value, endpoint);
    if (url.protocol !== 'https:' || url.origin !== endpoint.origin || url.username || url.password) throw new Error('upload-route-unavailable');
    return url.href;
  }
  function uploadPhoto(url, file, id, index, progress) {
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open('PUT', url, true);
      request.withCredentials = false;
      request.responseType = 'json';
      request.timeout = 600000;
      request.setRequestHeader('Content-Type', 'application/octet-stream');
      request.upload.addEventListener('progress', event => {
        const total = event.lengthComputable ? event.total : file.size;
        if (total > 0) progress(Math.min(99, Math.floor(event.loaded / total * 100)));
      });
      request.onload = () => {
        const receipt = request.response;
        if (request.status < 200 || request.status >= 300 || receipt?.uploaded !== true || receipt.inquiryId !== id || receipt.index !== index) {
          reject(new Error('upload-unconfirmed')); return;
        }
        progress(100); resolve();
      };
      request.onerror = request.ontimeout = request.onabort = () => reject(new Error('upload-unavailable'));
      request.send(file);
    });
  }
  async function submitPhotos(form, state, type, endpoint, verification) {
    const fields = Object.fromEntries(state.snapshot.pairs);
    fields['contact[Inquiry reference]'] = state.id;
    if (type.startsWith('koval_')) fields['contact[request_id]'] = state.id;
    const started = await requestJSON(`${endpoint.href.replace(/\/$/, '')}/begin`, {
      method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ inquiryId: state.id, formType: type, 'cf-turnstile-response': verification,
        website: state.snapshot.website, fields,
        files: state.snapshot.files.map(([, file]) => ({ filename: file.name, size: file.size })) })
    });
    const plan = started.receipt;
    if (!started.response.ok || plan?.ready !== true || plan.inquiryId !== state.id || plan.formType !== type ||
        !Array.isArray(plan.uploads) || plan.uploads.length !== state.snapshot.files.length ||
        plan.uploads.some((upload, index) => upload.index !== index || typeof upload.url !== 'string' || typeof upload.stored !== 'boolean') ||
        typeof plan.finalizeUrl !== 'string') throw new Error('upload-plan-unconfirmed');
    const finalizeURL = intakeURL(plan.finalizeUrl, endpoint);
    // Validate every route before sending any buyer photo. Each request carries
    // one File directly; no combined multipart body or in-browser file copies.
    const uploads = plan.uploads.map(upload => ({ ...upload, url: intakeURL(upload.url, endpoint) }));
    for (const upload of uploads) {
      if (upload.stored) continue;
      let lastPercent = -1;
      const progress = percent => {
        if (percent === lastPercent) return;
        lastPercent = percent;
        status(form, 'working', text('uploading', 'Uploading photo {current} of {total} — {percent}%')
          .replace('{current}', String(upload.index + 1)).replace('{total}', String(uploads.length)).replace('{percent}', String(percent)));
      };
      progress(0);
      await uploadPhoto(upload.url, state.snapshot.files[upload.index][1], state.id, upload.index, progress);
    }
    status(form, 'working', text('finishing', 'Finishing your request…'));
    return requestJSON(finalizeURL, { method: 'POST', headers: { Accept: 'application/json' } });
  }
  async function submit(form) {
    let state = states.get(form);
    if (!state) { state = {}; states.set(form, state); }
    if (state.busy) return;
    const type = formType(form);
    form.dispatchEvent(new CustomEvent('hok:inquiry-prepare', { bubbles: true }));
    if (!TYPES.has(type) || !validate(form, type)) return;
    const next = snapshot(form, type);
    const photosChanged = state.snapshot && next.files.some(([, file], index) => file !== state.snapshot.files[index]?.[1]);
    if (state.accepted && state.fingerprint === next.fingerprint && !photosChanged) return;
    if (state.fingerprint !== next.fingerprint || photosChanged) {
      state.id = crypto.randomUUID(); state.fingerprint = next.fingerprint; state.snapshot = next; state.accepted = false;
    }
    if (!UUID.test(state.id || '')) throw new Error('inquiry-id-unavailable');
    const id = state.id;
    form.querySelectorAll('[data-stainless-inquiry-reference], [data-solar-inquiry-reference], [data-laser-inquiry-reference], [data-hqc-inquiry-reference], [data-koval-request-id]').forEach(input => { input.value = id; });
    const record = measurement(form, type, id);
    state.busy = true; controls(form, true);
    status(form, 'working', text('working', 'Sending your request…'));
    try {
      const endpoint = new URL(config.endpoint || '', location.origin);
      if (endpoint.protocol !== 'https:' || !config.sitekey || /REPLACE_WITH/.test(config.sitekey + endpoint.href)) throw new Error('configuration-unavailable');
      const verification = await token(form);
      let result;
      if (state.snapshot.files.length) result = await submitPhotos(form, state, type, endpoint, verification);
      else {
        const body = new FormData();
        body.append('inquiryId', id); body.append('formType', type); body.append('cf-turnstile-response', verification);
        body.append('website', state.snapshot.website);
        for (const [key, value] of state.snapshot.pairs) body.append(key, value);
        body.append('contact[Inquiry reference]', id);
        if (type.startsWith('koval_')) body.append('contact[request_id]', id);
        result = await requestJSON(endpoint.href, { method: 'POST', body, headers: { Accept: 'application/json' } });
      }
      const { response, receipt } = result;
      if (!response.ok || receipt?.accepted !== true || receipt.inquiryId !== id || receipt.formType !== type) throw new Error('acceptance-unconfirmed');
      state.accepted = true;
      accepted.set(id, record); measure(record);
      status(form, 'success', text(type === 'koval_rendering' ? 'rendering_success' : 'success', type === 'koval_rendering' ? 'Your request and site photos have been received. Dave will follow up about your rendering.' : 'Thank you. Your request has been received. We’ll follow up using the contact details you provided.'));
      form.dispatchEvent(new CustomEvent('hok:inquiry-accepted', { bubbles: true, detail: { inquiryId: id, formType: type } }));
    } catch {
      status(form, 'error', text('error', 'We couldn’t confirm your request. Your details are still here. Please try Send again, or email support@houseofkoval.com.'));
    } finally {
      state.busy = false; controls(form, false);
      if (state.accepted) form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach(button => { button.disabled = true; });
    }
  }
  function resetAccepted(form) {
    const state = states.get(form);
    if (state?.accepted && !state.busy) {
      state.accepted = false;
      form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach(button => { button.disabled = false; });
      const node = form.querySelector('[data-hok-inquiry-status]'); if (node) node.hidden = true;
      form.dataset.hokInquiryState = 'ready';
    }
    form.querySelectorAll('input[type="file"]').forEach(input => input.setCustomValidity(''));
  }
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-hok-inquiry')) return;
    event.preventDefault(); event.stopImmediatePropagation();
    submit(form).catch(() => status(form, 'error', text('error', 'We couldn’t confirm your request. Your details are still here. Please try again or email support@houseofkoval.com.')));
  }, true);
  for (const name of ['input', 'change']) document.addEventListener(name, event => {
    const form = event.target.closest?.('form[data-hok-inquiry]'); if (form) resetAccepted(form);
  });
  document.addEventListener('hok:quote-prepared', event => { const form = event.target.closest?.('form[data-hok-inquiry]'); if (form) resetAccepted(form); });
  document.addEventListener('focusin', event => {
    if (event.target.closest?.('form[data-hok-inquiry]') && config.sitekey && !/REPLACE_WITH/.test(config.sitekey)) loadTurnstile().catch(() => {});
  });
  const retryMeasurement = () => accepted.forEach(measure);
  document.addEventListener('visitorConsentCollected', retryMeasurement);
  document.addEventListener('koval:privacy-ready', retryMeasurement);
  window.addEventListener('pageshow', retryMeasurement);
  let attempts = 0;
  const tagTimer = setInterval(() => { retryMeasurement(); if (++attempts >= 60) clearInterval(tagTimer); }, 1000);
  if (!window.Shopify?.customerPrivacy && typeof window.Shopify?.loadFeatures === 'function') {
    try { window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], retryMeasurement); } catch { /* Measurement remains closed. */ }
  }
  window.HOKInquiry = { version: 1 };
})();
